import { defineStore } from 'pinia';
import axios from 'axios';

import type { AuthResponseDTO, Role, UserDTO } from '@krafta/shared-types';
import { AUTH_SESSION_KEY, AUTH_SESSION_EXPIRED_EVENT, parseStoredAuthSession, type StoredAuthSession } from '@shared/context/authSessionStorage';
import { resolveApiBase } from '@shared/api/apiBase';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  refreshTokenExpiresAt: string | null;
  user: UserDTO | null;
  loading: boolean;
  error: string | null;
  sessionGeneration: number;
}

const AUTH_STORAGE_KEYS = [AUTH_SESSION_KEY, 'krafta_admin_auth'];
const syncDisposers = new WeakMap<object, () => void>();

const API_BASE = resolveApiBase(import.meta.env.VITE_AUTH_API_URL);
const buildUrl = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE) {
    return normalizedPath;
  }
  if (normalizedPath.startsWith(API_BASE)) {
    return normalizedPath;
  }
  return `${API_BASE}${normalizedPath}`;
};

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    refreshToken: null,
    refreshTokenExpiresAt: null,
    user: null,
    loading: false,
    error: null,
    sessionGeneration: 0
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.token && state.user),
    authHeaders: (state): Record<string, string> =>
      state.token
        ? {
            Authorization: `Bearer ${state.token}`
          }
        : {},
    hasRole: (state) => (role: Role) => Boolean(state.user?.roles?.includes(role)),
    hasAnyRole: (state) => (roles: Role[]) =>
      roles.some((role) => state.user?.roles?.includes(role))
  },
  actions: {
    applySession(session: StoredAuthSession | null) {
      this.$patch({ token: session?.token ?? null, user: session?.user ?? null,
        refreshToken: session?.refreshToken ?? null, refreshTokenExpiresAt: session?.refreshTokenExpiresAt ?? null,
        error: null, loading: false, sessionGeneration: this.sessionGeneration + 1 });
    },
    persistSession(payload: AuthResponseDTO) {
      const raw = JSON.stringify({ token: payload.accessToken, refreshToken: payload.refreshToken,
        refreshTokenExpiresAt: payload.refreshTokenExpiresAt, user: payload.user });
      const session = parseStoredAuthSession(raw);
      if (!session) throw new Error('Не удалось проверить вход. Войдите снова.');
      window.localStorage.setItem(AUTH_SESSION_KEY, raw);
      this.applySession(session);
      AUTH_STORAGE_KEYS.slice(1).forEach(key => window.localStorage.removeItem(key));
    },
    clearSession() {
      this.applySession(null);
      AUTH_STORAGE_KEYS.forEach(key => window.localStorage.removeItem(key));
    },
    setSessionFromResponse(payload: AuthResponseDTO) { this.persistSession(payload); },
    syncSession() {
      let session: StoredAuthSession | null = null;
      try { session = parseStoredAuthSession(window.localStorage.getItem(AUTH_SESSION_KEY)); } catch { /* Unreadable storage cannot establish an employee. */ }
      const current = this.token && this.user ? { token: this.token, refreshToken: this.refreshToken,
        refreshTokenExpiresAt: this.refreshTokenExpiresAt, user: this.user } : null;
      if (JSON.stringify(session) !== JSON.stringify(current)) this.applySession(session);
    },
    initSync() {
      syncDisposers.get(this)?.();
      const storage = (event: StorageEvent) => {
        if ((event.storageArea === null || event.storageArea === window.localStorage) &&
          (event.key === null || AUTH_STORAGE_KEYS.includes(event.key))) this.syncSession();
      };
      const focus = () => this.syncSession();
      window.addEventListener('storage', storage); window.addEventListener('focus', focus);
      window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, focus);
      const stop = () => { window.removeEventListener('storage', storage); window.removeEventListener('focus', focus); window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, focus); syncDisposers.delete(this); };
      syncDisposers.set(this, stop); return stop;
    },
    hydrate() {
      try {
        const raw = window.localStorage.getItem(AUTH_SESSION_KEY);
        if (raw !== null) { this.applySession(parseStoredAuthSession(raw)); return; }
        const legacy = parseStoredAuthSession(window.localStorage.getItem(AUTH_STORAGE_KEYS[1]));
        if (legacy) {
          window.localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(legacy));
          this.applySession(legacy); window.localStorage.removeItem(AUTH_STORAGE_KEYS[1]);
        } else this.applySession(null);
      } catch { this.applySession(null); }
    },
    captureSession() {
      try {
        const raw = window.localStorage.getItem(AUTH_SESSION_KEY);
        const stored = parseStoredAuthSession(raw);
        const current = this.token && this.user ? { token: this.token, refreshToken: this.refreshToken,
          refreshTokenExpiresAt: this.refreshTokenExpiresAt, user: this.user } : null;
        if ((raw !== null || syncDisposers.has(this)) && JSON.stringify(stored) !== JSON.stringify(current)) {
          this.syncSession(); return null;
        }
        return { generation: this.sessionGeneration, raw };
      }
      catch { this.applySession(null); return null; }
    },
    isSessionCurrent(captured: { generation: number; raw: string | null } | null) {
      if (!captured || captured.generation !== this.sessionGeneration) return false;
      try { return window.localStorage.getItem(AUTH_SESSION_KEY) === captured.raw; }
      catch { return false; }
    },
    async login(email: string, password: string) {
      const captured = this.captureSession();
      if (!captured) return;
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.post(buildUrl('/auth/login'), { email, password });
        if (this.isSessionCurrent(captured)) this.setSessionFromResponse(response.data as AuthResponseDTO);
      } catch (error) {
        if (!this.isSessionCurrent(captured)) { this.syncSession(); return; }
        this.error =
          axios.isAxiosError(error) && error.response?.data?.message
            ? String(error.response.data.message)
            : 'Ошибка авторизации';
        this.token = null;
        this.user = null;
        this.refreshToken = null;
        this.refreshTokenExpiresAt = null;
      } finally {
        if (this.isSessionCurrent(captured)) this.loading = false;
        else this.syncSession();
      }
    },
    async loginWithQrToken(qrToken: string) {
      const captured = this.captureSession();
      if (!captured) return;
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.post(buildUrl('/auth/login-qr'), { qrToken });
        if (this.isSessionCurrent(captured)) this.setSessionFromResponse(response.data as AuthResponseDTO);
      } catch (error) {
        if (!this.isSessionCurrent(captured)) { this.syncSession(); return; }
        this.error =
          axios.isAxiosError(error) && error.response?.data?.message
            ? String(error.response.data.message)
            : 'Ошибка входа по коду доступа';
        this.token = null;
        this.user = null;
        this.refreshToken = null;
        this.refreshTokenExpiresAt = null;
      } finally {
        if (this.isSessionCurrent(captured)) this.loading = false;
        else this.syncSession();
      }
    },
    async logout() {
      const captured = this.captureSession();
      if (!captured) return;
      try {
        if (this.refreshToken) {
          await axios.post(buildUrl('/auth/logout'), { refreshToken: this.refreshToken });
        }
      } catch {
        // ignore logout errors
      } finally {
        if (this.isSessionCurrent(captured)) this.clearSession();
        else this.syncSession();
      }
    }
  }
});

export type AuthStore = ReturnType<typeof useAuthStore>;
